
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TeamUser } from "./UserListItem";
import { Loader2, AlertTriangle } from "lucide-react";

interface DeleteUserDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirmDelete: (password: string, notes: string) => Promise<void>;
  user: TeamUser | null;
}

export default function DeleteUserDialog({
  isOpen,
  onOpenChange,
  onConfirmDelete,
  user,
}: DeleteUserDialogProps) {
  const [adminPassword, setAdminPassword] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user) return null;

  const handleConfirmDelete = async () => {
    if (!adminPassword) {
      setError("Admin password is required");
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      await onConfirmDelete(adminPassword, notes);
      // Reset form and close dialog
      setAdminPassword("");
      setNotes("");
      onOpenChange(false);
    } catch (err: any) {
      setError(err.message || "Failed to delete user");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-red-600 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Confirm User Deletion
          </AlertDialogTitle>
          <AlertDialogDescription>
            You are about to delete user <span className="font-medium">{user.name}</span> ({user.email}). 
            This action cannot be undone and will remove all data associated with this user.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-md text-sm">
              {error}
            </div>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="admin-password" className="text-sm font-medium">
              Enter your admin password to confirm
            </Label>
            <Input
              id="admin-password"
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              placeholder="Your admin password"
              className="w-full"
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="deletion-notes" className="text-sm font-medium">
              Deletion notes (optional)
            </Label>
            <Textarea
              id="deletion-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reason for deletion"
              className="w-full min-h-24"
              disabled={isSubmitting}
            />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSubmitting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleConfirmDelete();
            }}
            className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Deleting...
              </>
            ) : (
              "Delete User"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
